import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import API from '../services/api';

const buildQrCandidates = (value = '', preferred = '') => {
  const text = String(value || '').trim();
  const first = String(preferred || '').trim();
  const encoded = encodeURIComponent(text);
  const list = [
    first,
    text ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&format=png&margin=1&data=${encoded}` : '',
    text ? `https://quickchart.io/qr?size=280&text=${encoded}` : '',
    text ? `https://chart.googleapis.com/chart?cht=qr&chs=280x280&chl=${encoded}` : '',
  ].filter(Boolean);
  return [...new Set(list)];
};

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const statusTone = (status = '') => {
  if (status === 'success') return 'success';
  if (status === 'failed' || status === 'error' || status === 'cancelled') return 'danger';
  if (status === 'pending' || status === 'created') return 'warning';
  return 'secondary';
};

const PhonePePaymentPage = () => {
  const query = useQuery();
  const initialTxn = query.get('merchantTransactionId') || '';
  const initialPayUrl = query.get('payUrl') || '';
  const initialQrData = query.get('qrData') || '';
  const initialQrImageUrl = query.get('qrImageUrl') || '';
  const createEndpoint = query.get('createEndpoint') || '/phonepe/create-phonepe-payment';
  const statusEndpoint = query.get('statusEndpoint') || '/phonepe/status';

  const [amount, setAmount] = useState('1');
  const [purpose, setPurpose] = useState('Portal payment');
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState('');
  const [merchantTransactionId, setMerchantTransactionId] = useState(initialTxn);
  const [status, setStatus] = useState('');
  const [payUrl, setPayUrl] = useState(initialPayUrl);
  const [qrData, setQrData] = useState(initialQrData);
  const [qrImageUrl, setQrImageUrl] = useState(initialQrImageUrl);
  const [qrCandidateIndex, setQrCandidateIndex] = useState(0);
  const [redirecting, setRedirecting] = useState(false);

  const qrCandidates = useMemo(
    () => buildQrCandidates(qrData || payUrl, qrImageUrl),
    [qrData, payUrl, qrImageUrl]
  );
  const activeQrImageUrl = qrCandidates[qrCandidateIndex] || '';

  const checkStatus = async (txnId = merchantTransactionId, { silent = false } = {}) => {
    if (!txnId) return;
    setStatusLoading(true);
    if (!silent) setError('');
    try {
      const { data } = await API.get(`${statusEndpoint}/${encodeURIComponent(txnId)}`);
      setStatus(data?.status || 'pending');
    } catch (err) {
      setStatus((prev) => prev || 'pending');
      if (!silent) {
        const detail =
          err?.response?.data?.error?.message ||
          err?.response?.data?.error?.detail ||
          err?.response?.data?.message ||
          'Failed to fetch PhonePe status';
        setError(detail);
      }
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    if (initialTxn) {
      setMerchantTransactionId(initialTxn);
      checkStatus(initialTxn, { silent: true });
    }
    if (initialPayUrl) setPayUrl(initialPayUrl);
    if (initialQrData) setQrData(initialQrData);
    if (initialQrImageUrl) setQrImageUrl(initialQrImageUrl);
    setQrCandidateIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTxn, initialPayUrl, initialQrData, initialQrImageUrl]);

  const startPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await API.post(createEndpoint, {
        amount: Number(amount),
        purpose,
      });
      const txnId = data?.merchantTransactionId || '';
      const redirect = data?.redirectUrl || data?.payUrl || '';
      const qrText = data?.qrData || redirect || '';
      const qrUrl =
        data?.qrImageUrl ||
        (qrText
          ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&format=png&margin=1&data=${encodeURIComponent(
              qrText
            )}`
          : '');
      setMerchantTransactionId(txnId);
      setPayUrl(redirect);
      setQrData(qrText);
      setQrImageUrl(qrUrl);
      setQrCandidateIndex(0);
      setStatus('created');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to initiate PhonePe payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-3">
      <Card className="shadow-sm">
        <Card.Header className="d-flex align-items-center gap-2 fw-semibold">
          <span className="material-symbols-outlined">payments</span>
          PhonePe Payment Gateway
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {(activeQrImageUrl || payUrl) ? (
            <Alert variant="info" className="mb-3">
              Hosted PhonePe page kabhi-kabhi QR properly render nahi karti. Is page par jo QR dikh raha hai usse direct pay karna better rahega.
            </Alert>
          ) : null}

          {!payUrl ? (
            <Form onSubmit={startPayment} className="mb-3">
              <div className="row g-3">
                <div className="col-md-4">
                  <Form.Label>Amount (INR)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-8">
                  <Form.Label>Purpose</Form.Label>
                  <Form.Control
                    type="text"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    maxLength={120}
                  />
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-3">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Starting...' : 'Generate QR'}
                </Button>
              </div>
            </Form>
          ) : null}

          {activeQrImageUrl ? (
            <Card className="border mb-3 shadow-sm">
              <Card.Body className="text-center">
                <div className="fw-semibold mb-1">Scan and Pay</div>
                <div className="small text-muted mb-2">Use this QR with PhonePe, GPay, Paytm, or any UPI app. This is the recommended payment method.</div>
                <img
                  src={activeQrImageUrl}
                  alt="PhonePe payment QR"
                  style={{ width: 260, maxWidth: '100%', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', padding: 10 }}
                  referrerPolicy="no-referrer"
                  onError={() => {
                    setQrCandidateIndex((prev) => (prev + 1 < qrCandidates.length ? prev + 1 : prev));
                  }}
                />
                <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
                  <Button size="sm" variant="success" onClick={() => checkStatus(merchantTransactionId)}>
                    I Have Paid, Check Status
                  </Button>
                  {payUrl ? (
                    <Button size="sm" variant="outline-primary" href={payUrl} target="_blank" rel="noreferrer">
                      Open Hosted Page
                    </Button>
                  ) : null}
                  {qrData ? (
                    <Button size="sm" variant="outline-secondary" onClick={() => navigator.clipboard?.writeText(qrData)}>
                      Copy UPI Link
                    </Button>
                  ) : null}
                </div>
                {qrData ? <div className="small text-muted mt-2">If PhonePe hosted page looks broken, use this QR or `Copy UPI Link`.</div> : null}
              </Card.Body>
            </Card>
          ) : null}

          <Card className="border-0 bg-light">
            <Card.Body>
              <div className="small text-muted mb-1">Merchant Transaction ID</div>
              <div className="fw-semibold mb-3">{merchantTransactionId || '—'}</div>

              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="small text-muted">Status:</div>
                <span className={`badge bg-${statusTone(status)}`}>{status || 'not checked'}</span>
              </div>

              <Button
                variant="outline-secondary"
                onClick={() => checkStatus()}
                disabled={!merchantTransactionId || statusLoading}
              >
                {statusLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />Checking...
                  </>
                ) : (
                  'Check Payment Status'
                )}
              </Button>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PhonePePaymentPage;
